import {
  Box,
  Center,
  Image,
  useColorModeValue,
  VStack,
} from '@cardboard-ui/react';
import { useItem, useItemHandleFragmentProps } from 'components/ItemHandle';
import React, { FC } from 'react';
import { RemirrorJSON } from 'remirror';
import { AppFileTypes } from 'utils/file/MIMETypeUtils';
import { ItemMenu_node$key } from '../../__generated__/ItemMenu_node.graphql';
import FormThumbnail from './FormThumbnail';
import PageThumbnail from './PageThumbnail';
interface StylishThumbnailProps {
  node: ItemMenu_node$key & useItemHandleFragmentProps['item'];
}

const StylishThumbnail: FC<StylishThumbnailProps> = ({ node }) => {
  const {
    type,
    icon,
    fileType,
    thumbnailSrc,
    newThumbnailSrc,
    contentType,
    contentData,
    questions,
    name,
  } = useItem({
    item: node,
  });

  const defaultIcon = (
    <Center w="100%" h="100%" fontSize="50px" color="blue.500">
      {icon}
    </Center>
  );

  switch (type) {
    case 'Folder':
      if (newThumbnailSrc) {
        return <FolderThumbnail url={newThumbnailSrc} />;
      }
      break;
    case 'FolderItem':
      if (contentType === 'File') {
        switch (fileType) {
          case AppFileTypes.IMAGE:
            if (thumbnailSrc) {
              return <ImageThumbnail url={thumbnailSrc} />;
            }
            break;
          default:
            if (newThumbnailSrc) {
              return <DocumentThumbnail url={newThumbnailSrc} />;
            }
        }
      } else if (contentType === 'Page') {
        return (
          <PageThumbnail
            data={contentData as RemirrorJSON}
            fallback={defaultIcon}
          />
        );
      } else if (contentType === 'Form') {
        return <FormThumbnail name={name} questions={questions || []} />;
      }
  }

  return defaultIcon;
};

export default StylishThumbnail;

interface ThumbnailProps {
  url: string;
}

export const ImageThumbnail: FC<ThumbnailProps> = ({ url }) => {
  return (
    <Box w="100%" h="100%">
      <VStack w="100%" h="100%" alignContent="center">
        <Image
          src={url}
          maxW="80%"
          maxH="80%"
          margin="auto"
          padding={1}
          border="Background"
          backgroundColor="white"
        />
      </VStack>
    </Box>
  );
};

export const DocumentThumbnail: FC<ThumbnailProps> = ({ url }) => {
  return (
    <Box w="100%" h="100%">
      <VStack w="100%" h="100%" alignContent="center" justifyContent="flex-end">
        <Image
          src={url}
          maxW="80%"
          maxH="95%"
          padding={1}
          border="Background"
          backgroundColor="white"
        />
      </VStack>
    </Box>
  );
};

const FolderThumbnail: FC<ThumbnailProps> = ({ url }) => {
  return (
    <Box w="100%" h="100%">
      <VStack
        w="100%"
        h="100%"
        sx={{
          '& svg': {
            display: 'block',
            maxW: '80%',
            maxH: '70%',
            margin: 'auto',
          },
        }}
        alignContent="center"
      >
        <FolderItemThumbnail url={url} />
      </VStack>
    </Box>
  );
};

const FolderItemThumbnail: FC<ThumbnailProps> = ({ url }) => {
  const value = useColorModeValue('blue.500', 'blue.500');
  return (
    <svg width="129" height="100" viewBox="0 0 262 205">
      <defs>
        <rect
          id="folderBody"
          x="-2.84217094e-14"
          y="34.893617"
          width="261.702128"
          height="170.106383"
          rx="5"
        ></rect>
      </defs>
      <path
        style={{ fill: 'var(--chakra-colors-blue-500' }}
        d="M-2.84217094e-14,42.4231787 L-2.84217094e-14,3.02439381 C-2.84217094e-14,1.35148738 1.35350291,5.3639035e-17 3.02313336,1.19806138e-16 L61.0638298,0 L92.9306002,0 C94.6059476,0 96.7353973,1.10186701 97.6950479,2.46108886 C97.6950479,2.46108886 104.207713,11.4998527 107.365298,17.4468085 L256.707966,17.4468085 C259.459474,17.4468085 261.702128,19.6846732 261.702128,22.4452192 L261.702128,186.916483 C261.702128,189.677176 259.466165,191.914894 256.707966,191.914894 L4.99416133,191.914894 C2.24265357,191.914894 -2.84217094e-14,189.677029 -2.84217094e-14,186.916483 L-2.84217094e-14,42.4231787 Z"
      ></path>
      <mask id="image-mask" fill="white">
        <use xlinkHref="#folderBody"></use>
      </mask>
      <use
        style={{ fill: 'var(--chakra-colors-blue-400' }}
        xlinkHref="#folderBody"
      ></use>
      <image
        preserveAspectRatio="xMidYMid slice"
        mask="url(#image-mask)"
        x="0"
        y="17"
        width="100%"
        height="100%"
        xlinkHref={url}
      ></image>
    </svg>
  );
};
